import React from 'react';
import { useTranslation } from 'react-i18next';
import { FilterMinimalStyleCircle16 } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { FilterContextMenu } from 'shared/components/ui/filter-context-menu';
import { useFilterState } from 'shared/hooks/use-filter-state';
import { useOnFilterOption } from 'shared/hooks/use-on-filter';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { FilterDocument } from 'shared/types/filter-document';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { Tag } from 'shared/types/tag';

export type FilterButtonProps = {
  filterDocument: FilterDocument;
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  updateFilterDocument: (filterDocument: FilterDocument) => void;
  onReset: () => void;
};

export const FilterButton: React.FC<FilterButtonProps> = ({
  filterDocument,
  goals,
  lifeAreas,
  tags,
  updateFilterDocument,
  onReset,
}) => {
  const filterButtonRef = React.useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const {
    menuOpen: filterMenuOpen,
    openMenu: openFilterMenu,
    closeMenu: closeFilterMenu,
  } = useOpenMenu();

  const { filters, activeFilters, activeOptions } = useFilterState(
    filterDocument,
    { lifeAreas, tags, goals },
  );

  const onFilter = useOnFilterOption(filterDocument, updateFilterDocument);

  const filterButtonLabel = activeFilters
    ? `${t('general.labels.filter')}: ${activeFilters}`
    : t('general.labels.filter');

  return (
    <>
      <Button
        ref={filterButtonRef}
        onClick={openFilterMenu}
        endIcon={FilterMinimalStyleCircle16}
        size={Sizes.Medium}
        variant={Variants.Text}
        color={activeFilters ? Colors.Primary : Colors.Inherit}
        selected={!!activeFilters}
        preventDrag
      >
        {filterButtonLabel}
      </Button>
      {filterMenuOpen && (
        <FilterContextMenu
          referenceElement={filterButtonRef}
          onOutside={closeFilterMenu}
          position="bottom-end"
          title={t('context-menus.filtering.task.title')}
          options={filters}
          activeOptions={activeOptions}
          onOption={onFilter}
          onReset={onReset}
        />
      )}
    </>
  );
};
